<script setup lang="ts">
import type { Report } from '@functions/types/analysis';
import type { DrillProgress, DrillSet } from '@functions/types/drills';
import { format } from 'date-fns';
import { collection, doc, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import { computed, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { useCollection, useDocument } from 'vuefire';
import DrillProgressComponent from '@/components/drills/DrillProgress.vue';
import { useDebugUser } from '@/composables/useDebugUser';
import { db, functions } from '@/firebase';
import Container from '@/layouts/Container.vue';

const route = useRoute();
const user = useDebugUser();
const toast = useToast();

interface RouteParams {
  drillSetId: string;
}

definePage({
  name: 'drill-set-progress',
});

// Get the drill set
const drillSetId = (route.params as RouteParams).drillSetId;
const drillSetRef = doc(db, `drills/${user.value?.uid}/sets/${drillSetId}`);
const { data: drillSet } = useDocument<DrillSet>(drillSetRef);

// State variables
const drillId = computed(() => drillSet.value?.id || '');
const selectedReportId = ref('');
const isComparing = ref(false);
const progress = ref<DrillProgress | null>(null);

// Check for reportId in query params (for direct linking from upload)
const reportIdFromQuery = computed(() => {
  return route.query.reportId as string | undefined;
});

// Get all reports for this user
const reportsQuery = computed(() => {
  if (!user.value?.uid) return null;

  return query(
    collection(db, `imports/${user.value.uid}/reports`),
    orderBy('createdAt', 'desc'),
  );
});

const { data: reports } = useCollection<Report>(reportsQuery);

// Auto-select report from query parameter
watchEffect(() => {
  if (reportIdFromQuery.value && reports.value?.length) {
    const reportExists = reports.value.some(report => report.id === reportIdFromQuery.value);
    if (reportExists) {
      selectedReportId.value = reportIdFromQuery.value;

      // Auto-compare if we have a report ID and drill data
      if (drillSet.value?.reportId) {
        compareProgress();
      }
    }
  }
});

// Get existing progress records for this drill
const progressQuery = computed(() => {
  if (!user.value?.uid || !drillId.value) return null;

  return query(
    collection(db, `drillProgress/${user.value.uid}/comparisons`),
    where('drillId', '==', drillId.value),
    orderBy('createdAt', 'desc'),
  );
});

const { data: progressRecords } = useCollection<DrillProgress>(progressQuery);

// Compare the progress between original report and selected report
async function compareProgress() {
  if (!user.value?.uid || !drillId.value || !drillSet.value?.reportId || !selectedReportId.value) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Missing information for comparison. Please select a report.',
      life: 3000,
    });
    return;
  }

  try {
    isComparing.value = true;

    const compareDrillProgress = httpsCallable(functions, 'compareDrillProgress');
    const result = await compareDrillProgress({
      userId: user.value.uid,
      drillId: drillId.value,
      originalReportId: drillSet.value.reportId,
      newReportId: selectedReportId.value,
    });

    progress.value = result.data as DrillProgress;
  }
  catch (error: any) {
    console.error('Error comparing progress:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.message || 'Failed to compare progress. Please try again later.',
      life: 5000,
    });
  }
  finally {
    isComparing.value = false;
  }
}

// View an existing progress record
function viewProgress(progressRecord: DrillProgress) {
  progress.value = progressRecord;
}
</script>

<template>
  <Toast />
  <Container>
    <template #title>
      Drill Progress
    </template>

    <div class="mb-4 flex items-center gap-2">
      <router-link
        :to="`/drills/${drillSetId}`"
        class="hover:text-gray-600 dark:hover:text-gray-400 flex items-center gap-1 text-black dark:text-white"
      >
        <i class="pi pi-arrow-left" />
        Back to Drill
      </router-link>
    </div>

    <div v-if="drillSet" class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
        Progress Tracking
      </h2>
      <p class="mt-2 text-gray-600 dark:text-gray-400">
        Compare your latest practice session to see how you've improved.
      </p>
    </div>

    <div v-if="drillSet && reports?.length && reports.length > 0" class="grid gap-6 md:grid-cols-3">
      <div class="md:col-span-1">
        <div class="bg-white p-4 rounded-lg shadow-sm mb-6">
          <h3 class="text-lg font-medium text-gray-900 mb-3">
            Compare New Session
          </h3>

          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Select a Report to Compare
            </label>
            <select
              v-model="selectedReportId"
              class="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="" disabled>
                Select a report
              </option>
              <option
                v-for="report in reports"
                :key="report.id"
                :value="report.id"
                :disabled="report.id === drillSet.reportId"
              >
                {{ format(new Date(report.createdAt.toDate()), 'MM/dd/yyyy') }} -
                {{ report.shotCount }} shots
              </option>
            </select>
          </div>

          <button
            class="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-70"
            :disabled="!selectedReportId || isComparing || selectedReportId === drillSet.reportId"
            @click="compareProgress"
          >
            <i v-if="isComparing" class="pi pi-spin pi-spinner mr-2" />
            {{ isComparing ? 'Comparing...' : 'Compare Progress' }}
          </button>
        </div>

        <div v-if="progressRecords?.length" class="bg-white p-4 rounded-lg shadow-sm">
          <h3 class="text-lg font-medium text-gray-900 mb-3">
            Previous Comparisons
          </h3>

          <div class="space-y-3">
            <div
              v-for="record in progressRecords"
              :key="record.id"
              class="border border-gray-200 rounded-md p-3 hover:bg-gray-50 cursor-pointer"
              @click="viewProgress(record)"
            >
              <div class="flex justify-between items-center mb-1">
                <span class="font-medium text-gray-900">
                  {{ format(new Date(record.createdAt.toDate()), 'MM/dd/yyyy') }}
                </span>
              </div>
              <p class="text-xs text-gray-500">
                Compared with {{ format(new Date(record.createdAt.toDate()), 'MM/dd/yyyy') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="md:col-span-2">
        <DrillProgressComponent
          v-if="progress"
          :progress="progress"
        />

        <div v-else class="bg-gray-50 p-8 rounded-lg border border-gray-200 flex flex-col items-center justify-center h-80">
          <div class="text-gray-400 mb-2 text-6xl">
            <i class="pi pi-chart-line" />
          </div>
          <h3 class="text-xl font-medium text-gray-700 mb-2">
            No Progress Data Yet
          </h3>
          <p class="text-gray-500 text-center mb-4">
            Select a report to compare with your original session to see your progress.
          </p>
        </div>
      </div>
    </div>

    <div v-else-if="!reports?.length" class="flex min-h-[40vh] items-center justify-center flex-col">
      <p class="text-gray-600 dark:text-gray-400 mb-4">
        You need more sessions to compare progress. Please upload a new training session.
      </p>
      <router-link
        :to="`/upload?drillSetId=${drillSetId}`"
        class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
      >
        Upload Training Session
      </router-link>
    </div>

    <div v-else-if="!drillSet" class="flex min-h-[60vh] items-center justify-center flex-col gap-2">
      <div class="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      <p class="text-gray-900 dark:text-gray-200">
        Loading drill data...
      </p>
    </div>
  </Container>
</template>
